import React from 'react';

import { IconArrowDropDown } from '@/components/icons/IconArrowDropDown';
import { IconArrowDropUp } from '@/components/icons/IconArrowDropUp';

import styles from './QuantitySelector.module.scss';

export type Props = {
  value: number | null;
  min: number;
  max: number;
  canIncrease?: boolean;
  canDecrease?: boolean;
  showZeroQuantity?: boolean;
  onChange: (value: number) => void;
};

export const QuantitySelector = (props: Props) => {
  const { value, min, max, canIncrease, canDecrease, showZeroQuantity, onChange } = props;

  const onPlusButtonClick = () => {
    if (value !== null && value + 1 <= max && canIncrease) {
      onChange(value + 1);
    }
  };

  const onMinusButtonClick = () => {
    if (value && value - 1 >= min && canDecrease) {
      onChange(value - 1);
    }
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = event.target.value;

    if (isNaN(Number(targetValue))) {
      return;
    }

    if (!canDecrease && canIncrease && Math.abs(parseInt(targetValue, 10)) < min) {
      onChange(min);
    }

    if (!canDecrease && canIncrease && Math.abs(parseInt(targetValue, 10)) >= min) {
      onChange(Math.abs(parseInt(targetValue, 10)));
    }

    if (canDecrease && !canIncrease && Math.abs(parseInt(targetValue, 10)) <= max) {
      onChange(Math.abs(parseInt(targetValue, 10)));
    }

    if (canDecrease && !canIncrease && Math.abs(parseInt(targetValue, 10)) > max) {
      onChange(max);
    }

    if (!canIncrease && !canDecrease) {
      return;
    }

    const inputValue = targetValue.includes('-')
      ? Number(targetValue.split('-')[1])
      : Number(targetValue);

    switch (true) {
      case inputValue > max:
        onChange(max);
        break;

      case inputValue < min:
        onChange(min);
        break;

      default:
        onChange(inputValue);
    }
  };

  const allowZeroValue = showZeroQuantity && value === 0;

  return (
    <div className={styles.wrapper}>
      {value || allowZeroValue ? (
        <input
          data-testid="quantity-input"
          max={max}
          min={min}
          onChange={onChangeInput}
          type="number"
          value={value.toString()}
        />
      ) : (
        <input data-testid="quantity-input" onChange={onChangeInput} type="text" value="-" />
      )}

      <div className={styles.buttons}>
        <div
          className={styles.button}
          data-testid="quantity-up"
          onClick={() => onPlusButtonClick()}
        >
          <IconArrowDropUp classes={{ container: styles.container, icon: styles.icon }} />
        </div>
        <div
          className={styles.button}
          data-testid="quantity-down"
          onClick={() => onMinusButtonClick()}
        >
          <IconArrowDropDown classes={{ container: styles.container, icon: styles.icon }} />
        </div>
      </div>
    </div>
  );
};
